import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Check, Clock } from "lucide-react";
import { SectionHead, Reveal, EASE } from "./Reveal";
import { useContent } from "@/context/ContentContext";
import { VEHICLES, activeAddons, formatEur, formatDuration } from "@/lib/site";

export default function Calculator({ onApply }) {
  const c = useContent();
  const t = c.texts;
  const [vehicle, setVehicle] = useState("m");
  const [serviceId, setServiceId] = useState("aufbereitung");
  const [addons, setAddons] = useState([]);
  const [seats, setSeats] = useState(2);

  const pkg = c.packages.find((p) => p.id === serviceId) || c.packages[0];
  const vehicleObj = VEHICLES.find((v) => v.id === vehicle);
  const addonsList = activeAddons(c);

  const addonPrice = (a) =>
    a.prices_per_class && a.prices_per_class[vehicle] != null ? a.prices_per_class[vehicle] : a.price;

  const price = useMemo(() => {
    let p = pkg?.prices?.[vehicle] ?? 0;
    for (const a of addonsList) {
      if (!addons.includes(a.id)) continue;
      p += a.per_seat ? addonPrice(a) * seats : addonPrice(a);
    }
    return p;
  }, [pkg, vehicle, addons, seats, addonsList]);

  const duration = useMemo(() => {
    let m = pkg?.durations?.[vehicle] ?? 0;
    for (const a of addonsList) {
      if (!addons.includes(a.id)) continue;
      m += a.per_seat ? (a.minutes || 0) * seats : a.minutes || 0;
    }
    return m;
  }, [pkg, vehicle, addons, seats, addonsList]);

  const toggleAddon = (id) =>
    setAddons((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const apply = () => {
    const chosen = addonsList.filter((a) => addons.includes(a.id));
    const addonLabels = chosen.map((a) => a.name + (a.per_seat ? ` (${seats} ${seats === 1 ? "Sitz" : "Sitze"})` : ""));
    onApply({
      vehicle: vehicleObj.label,
      service: pkg?.name,
      addonIds: addons,
      estimated: `Voraussichtlich ab ${formatEur(price)}`,
      notes:
        `Gewünschter Tarif: ${pkg?.name} (${vehicleObj.label})` +
        (addonLabels.length ? ` inkl. ${addonLabels.join(", ")}` : ""),
    });
  };

  return (
    <section id="preise" data-testid="calculator-section" className="relative py-24 sm:py-32 bg-[#0C0C0E] border-y border-white/5">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow={t.calc_eyebrow} title={t.calc_title} sub={t.calc_sub} />

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-7 flex flex-col gap-8">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 mb-4">
                Schritt 1 · Fahrzeuggröße
              </p>
              <div data-testid="vehicle-type-select" className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {VEHICLES.map((v) => (
                  <button
                    key={v.id}
                    data-testid={`vehicle-option-${v.id}`}
                    onClick={() => setVehicle(v.id)}
                    className={`rounded-xl border p-4 text-left transition-all duration-300 ${
                      vehicle === v.id
                        ? "border-blue-500 bg-blue-500/10"
                        : "border-[#27272A] bg-[#121215] hover:border-zinc-600"
                    }`}
                  >
                    <span className={`font-mono text-sm font-bold ${vehicle === v.id ? "text-blue-400" : "text-zinc-400"}`}>
                      {v.code}
                    </span>
                    <span className="mt-1 block text-sm font-semibold text-zinc-100 leading-tight">{v.label}</span>
                    <span className="mt-0.5 block text-[11px] text-zinc-400">{v.example}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 mb-4">
                Schritt 2 · Leistung
              </p>
              <div className="flex flex-col gap-3">
                {c.packages.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`service-option-${p.id}`}
                    onClick={() => setServiceId(p.id)}
                    className={`flex items-center justify-between gap-4 rounded-xl border p-5 text-left transition-all duration-300 ${
                      pkg?.id === p.id
                        ? "border-blue-500 bg-blue-500/10"
                        : "border-[#27272A] bg-[#121215] hover:border-zinc-600"
                    }`}
                  >
                    <div>
                      <span className="block font-semibold text-zinc-100">{p.name}</span>
                      <span className="mt-1 block text-sm text-zinc-400">{p.desc}</span>
                    </div>
                    <span className="font-mono text-sm font-bold text-blue-400 whitespace-nowrap">
                      ab {formatEur(p.prices?.[vehicle] ?? 0)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 mb-4">
                Schritt 3 · Extras (optional)
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {addonsList.map((a) => (
                  <div key={a.id} className={a.per_seat ? "sm:col-span-2" : ""}>
                    <button
                      data-testid="service-option-checkbox"
                      onClick={() => toggleAddon(a.id)}
                      className={`flex w-full items-center justify-between gap-3 rounded-xl border p-4 text-left transition-all duration-300 ${
                        addons.includes(a.id)
                          ? "border-blue-500 bg-blue-500/10"
                          : "border-[#27272A] bg-[#121215] hover:border-zinc-600"
                      }`}
                    >
                      <div className="min-w-0">
                        <span className="flex items-center gap-2.5">
                          <span
                            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors ${
                              addons.includes(a.id) ? "border-blue-500 bg-blue-500" : "border-zinc-600"
                            }`}
                          >
                            {addons.includes(a.id) && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                          </span>
                          <span className="text-sm font-semibold text-zinc-100">{a.name}</span>
                        </span>
                        {a.desc && <span className="mt-1.5 block pl-8 text-xs leading-relaxed text-zinc-400">{a.desc}</span>}
                      </div>
                      <span className="font-mono text-sm font-bold text-blue-400 whitespace-nowrap">
                        +{formatEur(addonPrice(a))}
                        {a.per_seat ? " / Sitz" : ""}
                      </span>
                    </button>
                    {a.per_seat && addons.includes(a.id) && (
                      <div className="mt-2 flex items-center justify-between rounded-xl border border-blue-500/30 bg-[#0C0C0E] px-4 py-2.5">
                        <span className="text-sm text-zinc-300">Anzahl Sitze</span>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            data-testid="seat-decrement"
                            onClick={() => setSeats((s) => Math.max(1, s - 1))}
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-zinc-700 text-zinc-300 hover:border-blue-500 hover:text-blue-400 transition-colors"
                            aria-label="Weniger Sitze"
                          >
                            −
                          </button>
                          <span data-testid="seat-count" className="w-5 text-center font-mono text-sm font-bold text-zinc-100">
                            {seats}
                          </span>
                          <button
                            type="button"
                            data-testid="seat-increment"
                            onClick={() => setSeats((s) => Math.min(8, s + 1))}
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-zinc-700 text-zinc-300 hover:border-blue-500 hover:text-blue-400 transition-colors"
                            aria-label="Mehr Sitze"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 rounded-2xl border border-[#27272A] bg-gradient-to-b from-[#18181B] to-[#121215] p-8">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Dein Richtpreis</p>
              <div data-testid="calculated-price-display" className="mt-3 flex items-baseline gap-2">
                <span className="font-mono text-lg text-zinc-400">voraussichtlich ab</span>
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.span
                    key={price}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    className="font-display text-5xl sm:text-6xl font-extrabold text-blue-400 tracking-tight"
                  >
                    {price.toLocaleString("de-DE", { minimumFractionDigits: Number.isInteger(price) ? 0 : 2, maximumFractionDigits: 2 })}
                  </motion.span>
                </AnimatePresence>
                <span className="font-sans text-2xl sm:text-3xl font-semibold text-blue-400">€</span>
              </div>

              <div className="mt-6 flex flex-col gap-2 text-sm">
                <div className="flex items-center gap-2 text-zinc-300">
                  <Check className="h-4 w-4 text-blue-400" />
                  {pkg?.name}
                </div>
                <div className="flex items-center gap-2 text-zinc-300">
                  <Check className="h-4 w-4 text-blue-400" />
                  {vehicleObj.label}
                </div>
                {addonsList
                  .filter((a) => addons.includes(a.id))
                  .map((a) => (
                    <div key={a.id} className="flex items-center gap-2 text-zinc-300">
                      <Check className="h-4 w-4 text-blue-400" />
                      {a.name}
                      {a.per_seat ? ` (${seats} ${seats === 1 ? "Sitz" : "Sitze"})` : ""}
                    </div>
                  ))}
                <div className="flex items-center gap-2 text-zinc-400 mt-1">
                  <Clock className="h-4 w-4 text-zinc-400" />
                  Dauer: {formatDuration(duration)} (vor Ort)
                </div>
              </div>

              <p className="mt-5 text-xs leading-relaxed text-zinc-400">
                Den endgültigen Preis bestätigen wir nach Abstimmung zum Fahrzeugzustand – vor dem
                Termin.
              </p>

              <button
                data-testid="calculator-apply-button"
                onClick={apply}
                className="group mt-7 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-blue-600 text-sm font-semibold text-white hover:bg-blue-500 transition-colors"
              >
                Diesen Tarif anfragen
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
